import "@shoelace-style/shoelace/dist/themes/light.css";
import { setBasePath } from "@shoelace-style/shoelace/dist/utilities/base-path";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import { appState } from "./Api/parameters";
import "./App.css";
import { AppState, AppStateType } from "./enums/appState.enum";
import { setServerError, updateParametersData } from "./reduxSlices/parametersSlice";

import {
  doLoginThunk,
  hydrateStudentSession,
  selectStudent,
  selectToken,
  setFlagsSesion,
  setStudentInicializado,
  setToken,
  updateStudentData,
} from "./reduxSlices/studentSlice";
import {
  selectInscribedSubjects,
  selectSubjectsQuotas,
  setAllQuotasReadyToUse,
  setInscribedSubjectsWithoutFilters,
  setLoadingRequestInTransit,
} from "./reduxSlices/subjectsSlice";

//@ts-ignore
import { auth, getEstadoAlumno, getFlagsSesion } from "./Api/authentication";
import { getMateriasInscriptasAlumno } from "./Api/subject";
import useMateriasAlumno from "./hooks/useMateriasAlumno";
import { EstadoAlumnoCarrera } from "./enums/alumnoCarrera.enum";
import store, { useAppDispatch } from "./app/store";
import useMediaQueries from "./hooks/useMediaQuery";
import { updatePrioridadThunk } from "./reduxSlices/studentSlice";
import {
  selectInvalidarSemaforoSocketCount,
  setTabletDevice,
  setLoginCompleted,
} from "./reduxSlices/globalFlagsSlice";
import { useToastWarning } from "./hooks/useToast";
import AppRouter from "./router/AppRouter";
import usePinnedSubjects from "./hooks/usePinnedSubjects";
import { isTabletDevice } from "./utils/deviceHelpers";
import { isTokenExpired, scheduleAutoLogout } from "./utils/authHelpers";
import {
  clearSessionSnapshot,
  getHydratableSessionSnapshot,
  persistSessionFromStudent,
} from "./utils/sessionSnapshot";

function App() {
  // Configurar basePath para usar recursos locales en lugar de CDN
  // Los recursos se copian a /shoelace durante el build
  setBasePath(`${process.env.PUBLIC_URL || ""}/shoelace/`);
  const student = useSelector(selectStudent);
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const dispatch = useAppDispatch();
  const invalidarSemaforoSocketCount = useSelector(selectInvalidarSemaforoSocketCount);
  const showWarning = useToastWarning();

  const { refreshPinnedSubjects } = usePinnedSubjects();

  const [sessionExpired, setSessionExpired] = useState(false);
  const token = useSelector(selectToken);
  const invalidarSemaforoSocketCountLocal = useRef(invalidarSemaforoSocketCount);
  const hasSetOfertaFlag = useRef(false);
  const isCheckingUserInit = useRef(false);
  const lastInitializedToken = useRef<string | null>(null);
  const hasInitializedSubjects = useRef(false);
  const ofertaEnCurso = useRef<Promise<void> | null>(null);
  const lastInitializedStudentId = useRef<number | null>(null);
  const hasInitializedPrioridad = useRef(false);
  const studentPrioridadRef = useRef<boolean | null>(null);
  const checkUserInitIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Hook para obtener y actualizar materias
  const { fetchAndUpdateMaterias } = useMateriasAlumno(student.id, {
    updateTags: true,
    updatePinnedSubjects: true,
    refreshPinnedSubjects,
    updateLastUpdateTimestamp: true,
    hasSetOfertaFlag,
  });

  useEffect(() => {
    invalidarSemaforoSocketCountLocal.current = invalidarSemaforoSocketCount; // Actualiza referencia local
  }, [invalidarSemaforoSocketCount]);

  useEffect(() => {
    dispatch(setTabletDevice(isTabletDevice()));
  }, [dispatch]);

  const getTokenAndHydrateSession = () => {
    const tkn = localStorage.getItem("token");
    if (!tkn) return false;

    if (isTokenExpired(tkn)) {
      console.log("🕐 Token vencido al iniciar App, limpiando sesión");
      clearSessionSnapshot();
      localStorage.removeItem("token");
      setSessionExpired(true);
      return false;
    }

    dispatch(setToken(tkn));
    scheduleAutoLogout(tkn);

    const snapshot = getHydratableSessionSnapshot(tkn);
    if (snapshot) {
      console.log("♻️ Hidratando sesión desde snapshot (sin auth/doLogin)");
      dispatch(hydrateStudentSession(snapshot));
      dispatch(setLoginCompleted(true));
      return true;
    }
    return false;
  };

  const doLoginAndCheckBloqueos = async (email: string): Promise<void> => {
    console.log(`doLoginAndCheckBloqueos -> email ${email}`);
    await dispatch(
      doLoginThunk({
        email,
        useCampusTokenFallback: true,
        persistIdAP: true,
      }),
    );
  };

  // Mantener el ref actualizado con el valor de prioridad
  useEffect(() => {
    studentPrioridadRef.current = student.prioridad;
  }, [student.prioridad]);

  const persistStudentSession = useCallback(() => {
    const current = store.getState().student;
    if (!current.token || !current.id || !current.email) return;
    persistSessionFromStudent({
      token: current.token,
      id: current.id,
      email: current.email,
      completeName: current.completeName,
      studentInitState: current.studentInitState,
      listadoCarreras: current.listadoCarreras,
      carreraActual: current.carreraActual,
      mostrarHorarios: current.mostrarHorarios,
      mostrarAulas: current.mostrarAulas,
      bloqueoActivo: current.bloqueoActivo,
      bloqueoMensaje: current.bloqueoMensaje,
      bloqueoSubmensaje: current.bloqueoSubmensaje,
      debeAlertarSuperposicion: current.debeAlertarSuperposicion,
      debeFiltrarCursosConCupoNoAsignado: current.debeFiltrarCursosConCupoNoAsignado,
      debeConsultarCupos: current.debeConsultarCupos,
    });
  }, []);

  const updatePrioridadOnRefresh = useCallback(async () => {
    // Evitar ejecuciones duplicadas para el mismo student.id durante la inicialización
    if (hasInitializedPrioridad.current && lastInitializedStudentId.current === student.id && student.id !== 0) {
      console.log("🔄 updatePrioridadOnRefresh ya ejecutado para este student.id, omitiendo...");
      return;
    }
    hasInitializedPrioridad.current = true;
    lastInitializedStudentId.current = student.id;
    // Usar el ref para obtener el valor actual sin incluir student.prioridad en las dependencias
    const resultAction = await dispatch(updatePrioridadThunk(studentPrioridadRef.current));

    // Verificar si se agotaron los reintentos
    if (updatePrioridadThunk.rejected.match(resultAction)) {
      const errorPayload = resultAction.payload;
      if (errorPayload === "REINTENTOS_AGOTADOS") {
        showWarning(
          "No se pudo obtener la información de prioridad después de varios intentos. La página se refrescará automáticamente.",
        );
        setTimeout(() => {
          window.location.reload();
        }, 3500);
      }
    } else {
      // Persist debeConsultarCupos / flags actualizados por prioridad
      persistStudentSession();
    }
  }, [dispatch, student.id, showWarning, persistStudentSession]);

  useEffect(() => {
    const hidratoDesdeSnapshot = getTokenAndHydrateSession();
    if (!hidratoDesdeSnapshot) return;

    void (async () => {
      const flags = await getFlagsSesion();
      if (!flags) return;
      dispatch(setFlagsSesion(flags));
      persistStudentSession();
    })();
  }, []);

  const getUserId = async (): Promise<void> => {
    try {
      const alumno = await auth();
      await doLoginAndCheckBloqueos(alumno.email);

      if (alumno.error) {
        console.log("ERROR en la restpuuesta de Auth");

        dispatch(setServerError(true));
        throw new Error("Error de consulta.");
      }

      dispatch(
        updateStudentData({
          id: alumno.id,
          email: alumno.email,
          completeName: {
            name: alumno.nombre,
            lastName: alumno.apellido,
          },
        }),
      );
      // Persist after identity + doLogin data are in the store
      persistSessionFromStudent({
        ...store.getState().student,
        id: alumno.id,
        email: alumno.email,
        completeName: {
          name: alumno.nombre,
          lastName: alumno.apellido,
        },
        studentInitState: EstadoAlumnoCarrera.KV_INICIALIZADO,
      });
    } catch (error: any) {
      console.error(`getUserId error:>> ${error?.message || "Unknown error"}`);
      clearSessionSnapshot();
      setSessionExpired(true);
    }
  };

  const checkUserInitAndGetStudentData = async (): Promise<EstadoAlumnoCarrera | null> => {
    if (isCheckingUserInit.current) {
      console.log("⏳ checkUserInit ya en curso, omitiendo...");
      return null;
    }
    if (lastInitializedToken.current === token && student.id > 0 && student.studentInitState === EstadoAlumnoCarrera.KV_INICIALIZADO) {
      return EstadoAlumnoCarrera.KV_INICIALIZADO;
    }

    isCheckingUserInit.current = true;
    try {
      const res = await getEstadoAlumno();
      if (res?.error?.response?.status === 401) {
        console.error("checkUserInitAndGetStudentData error:>> 401 Unauthorized");
        clearIntervalCheckUserInit();
        clearSessionSnapshot();
        setSessionExpired(true);
        dispatch(setStudentInicializado(0));
        return null;
      }

      if (!res || typeof res.estado !== "number") {
        clearIntervalCheckUserInit();
        clearSessionSnapshot();
        localStorage.clear();
        await dispatch(setStudentInicializado(0));
        return null;
      }

      const estado = res.estado as EstadoAlumnoCarrera;
      if (estado === EstadoAlumnoCarrera.KV_INICIALIZADO) {
        clearIntervalCheckUserInit();

        // Sesión ya hidratada con identidad: no re-ejecutar auth/doLogin
        const current = store.getState().student;
        if (current.id > 0 && current.email) {
          dispatch(setStudentInicializado(EstadoAlumnoCarrera.KV_INICIALIZADO));
          lastInitializedToken.current = current.token || token;
          persistStudentSession();
        } else {
          // Marcar init antes de awaits largos para evitar carreras con effects
          dispatch(setStudentInicializado(EstadoAlumnoCarrera.KV_INICIALIZADO));
          await getUserId();
          lastInitializedToken.current = store.getState().student.token || token;
        }
      } else if (estado === EstadoAlumnoCarrera.INICIALIZACION_KV_PENDIENTE) {
        await dispatch(setStudentInicializado(EstadoAlumnoCarrera.INICIALIZACION_KV_PENDIENTE));
      } else {
        await dispatch(setStudentInicializado(estado));
      }

      return estado;
    } catch (err: any) {
      console.error(`checkUserInitAndGetStudentData error:>> ${err?.message || "Unknown error"}`);
      clearIntervalCheckUserInit();
      clearSessionSnapshot();
      setSessionExpired(true);
      return null;
    } finally {
      isCheckingUserInit.current = false;
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                  APP Mount                                 */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    // getToken();
    /* ----------------------------- set parameters ----------------------------- */
    appState()
      .then((res) => {
        if (Object.values(AppState).includes(res)) {
          dispatch(
            updateParametersData({
              appState: res as AppStateType,
              started: true,
            }),
          );
        } else {
          console.error("Estado no válido");
          dispatch(setServerError(true));
        }
      })
      .catch((err) => {
        console.log("ERROR :>>", err);
      });
  }, []);

  const clearIntervalCheckUserInit = () => {
    if (checkUserInitIntervalRef.current) {
      clearInterval(checkUserInitIntervalRef.current);
      checkUserInitIntervalRef.current = null;
    }
  };

  const initIntervalToCheckUserInit = () => {
    if (checkUserInitIntervalRef.current !== null) return;
    checkUserInitIntervalRef.current = setInterval(async () => {
      const estado = await checkUserInitAndGetStudentData();
      if (estado === EstadoAlumnoCarrera.KV_INICIALIZADO) {
        clearIntervalCheckUserInit();
      }
    }, 5000);
  };

  // Coordina la validación inicial y el polling del estado pendiente.
  // La consulta no depende de las carreras: permite recuperar sesiones antiguas
  // que sólo tenían el token persistido.
  useEffect(() => {
    if (!token || token.length <= 5) {
      clearIntervalCheckUserInit();
      return;
    }

    let cancelled = false;
    (async () => {
      const estado = await checkUserInitAndGetStudentData();
      if (!cancelled && estado === EstadoAlumnoCarrera.INICIALIZACION_KV_PENDIENTE) {
        initIntervalToCheckUserInit();
      }
    })();

    return () => {
      cancelled = true;
      clearIntervalCheckUserInit();
    };
  }, [token]);

  /* -------------------------------------------------------------------------- */
  /*                           If USER: SET STUDENT STORE DATA                  */
  /* -------------------------------------------------------------------------- */

  const getStoredSubjects = useCallback(async () => {
    // Las invocaciones duplicadas esperan la solicitud en curso: la oferta arrastra
    // la consulta de cupos, así que volver a pedirla los duplicaría.
    if (hasInitializedSubjects.current && lastInitializedStudentId.current === student.id && student.id !== 0) {
      console.log("🔄 getStoredSubjects ya ejecutado para este student.id, reutilizando la oferta en curso...");
      await ofertaEnCurso.current;
      return;
    }
    hasInitializedSubjects.current = true;
    lastInitializedStudentId.current = student.id;

    const solicitud = (async () => {
      /* ----------------------------- GET Inscriptas ----------------------------- */
      const inscriptas = await getMateriasInscriptasAlumno(student.id);

      // Almacenar materias inscritas sin filtro primero
      if (inscriptas.status === 200 && inscriptas.data) {
        dispatch(setInscribedSubjectsWithoutFilters(inscriptas.data));
      }

      /* -------------------------- GET Oferta + CUPOS --------------------------- */
      await fetchAndUpdateMaterias({ cupos: "completos" });
    })();

    ofertaEnCurso.current = solicitud;
    await solicitud;
  }, [student.id, dispatch, fetchAndUpdateMaterias]);

  const handleOnStudentInitApp = useCallback(async () => {
    if (student.id && !student.bloqueoActivo && student.listadoCarreras.length > 0 && (student.listadoCarreras.length > 1 ? student.carreraActual?.length > 0 : true)) {
      /* ---------------------------- UPDATE PRIORIDAD ---------------------------- */
      // El thunk ahora maneja la validación del token internamente (await para leer F_DESDE actualizado)
      await updatePrioridadOnRefresh();
      /* ------------------- STORE SUBJECTS: inscriptas, oferta y cupos ----------- */
      await getStoredSubjects();
    }
  }, [student.id, student.bloqueoActivo, student.listadoCarreras.length, student.carreraActual, updatePrioridadOnRefresh, getStoredSubjects]);

  useEffect(() => {
    // Resetear flags cuando cambia el student.id para permitir nueva inicialización
    if (student.id && lastInitializedStudentId.current !== student.id) {
      hasInitializedSubjects.current = false;
      hasInitializedPrioridad.current = false;
      ofertaEnCurso.current = null;
    }
  }, [student.id]);

  useEffect(() => {
    handleOnStudentInitApp();
  }, [handleOnStudentInitApp]);

  /* -------------------------------------------------------------------------- */
  /*                     TIMEOUT: forzar readyToUse en cupos                    */
  /* -------------------------------------------------------------------------- */
  const subjectsQuotas = useSelector(selectSubjectsQuotas);
  const quotasTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const quotaKeys = Object.keys(subjectsQuotas);
    if (quotaKeys.length === 0) return;

    const allReady = quotaKeys.every((key) => subjectsQuotas[Number(key)].readyToUse);
    if (allReady) {
      if (quotasTimeoutRef.current) {
        clearTimeout(quotasTimeoutRef.current);
        quotasTimeoutRef.current = null;
      }
      return;
    }

    if (!quotasTimeoutRef.current) {
      quotasTimeoutRef.current = setTimeout(() => {
        console.log("🔄 forzar readyToUse en cupos - supero el tiempo de espera de 60 segundos");
        dispatch(setAllQuotasReadyToUse());
        quotasTimeoutRef.current = null;
      }, 60000);
    }

    return () => {
      if (quotasTimeoutRef.current) {
        clearTimeout(quotasTimeoutRef.current);
        quotasTimeoutRef.current = null;
      }
    };
  }, [subjectsQuotas, dispatch]);

  /* -------------------------------------------------------------------------- */
  /*                             SNAPSHOT Inscribed                             */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    dispatch(setLoadingRequestInTransit(false));
    if (inscribedSubjects === null) {
      localStorage.removeItem("initialSnapshot");
    }
    const snapshot = localStorage.getItem("initialSnapshot");
    if (!snapshot && inscribedSubjects) {
      localStorage.setItem("initialSnapshot", JSON.stringify(inscribedSubjects));
    }
  }, [inscribedSubjects]);

  const { lg } = useMediaQueries();

  return <AppRouter lg={lg} sessionExpired={sessionExpired} mostrarHorarios={student.mostrarHorarios} />;
}

export default App;
